import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { type DropdownOption } from '../dropdown/dropdown.component';

@Component({
  selector: 'shared-ui-multi-select',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.id]': 'null',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
})
export class MultiSelectComponent<T = string> implements ControlValueAccessor {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly id = input.required<string>();
  readonly label = input.required<string>();
  readonly options = input.required<readonly DropdownOption<T>[]>();
  readonly placeholder = input('Bitte auswählen');
  readonly optional = input(false);
  readonly isDisabled = input(false);
  readonly isInvalid = input(false);
  readonly errorText = input('');
  readonly hintText = input('');

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly value = signal<T[]>([]);
  protected readonly isOpened = signal(false);
  private readonly cvaDisabled = signal(false);

  private onChange: (value: T[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  @HostListener('document:click', ['$event'])
  protected closeWhenClickedOutside(event: MouseEvent): void {
    if (this.elementRef.nativeElement.contains(event.target as Node)) return;

    this.closeMenu();
  }

  @HostListener('keydown.escape')
  protected closeOnEscape(): void {
    this.closeMenu();
  }

  writeValue(value: readonly T[] | null): void {
    this.value.set([...(value ?? [])]);
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
    if (isDisabled) this.closeMenu();
  }

  protected disabled(): boolean {
    return this.isDisabled() || this.cvaDisabled();
  }

  protected filteredOptions(): readonly DropdownOption<T>[] {
    const search = this.searchControl.value.trim().toLowerCase();

    if (!search) return this.options();

    return this.options().filter((option) => option.label.toLowerCase().includes(search));
  }

  protected selectedCount(): number {
    return this.value().length;
  }

  protected selectedCountLabel(): string {
    return `${this.selectedCount()} ausgewählt`;
  }

  protected showInvalid(): boolean {
    return !this.disabled() && this.isInvalid();
  }

  protected triggerClass(): string {
    if (this.showInvalid()) {
      return 'border-red-400 bg-white text-gray-700 focus:border-red-400 focus:ring-1 focus:ring-red-400';
    }

    if (this.disabled()) {
      return 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400';
    }

    return 'border-gray-300 bg-white text-gray-700 focus:border-green-800 focus:ring-1 focus:ring-green-800';
  }

  protected toggleMenu(): void {
    if (this.disabled()) return;

    this.isOpened.update((opened) => !opened);
    this.searchControl.setValue('', { emitEvent: false });

    if (!this.isOpened()) {
      this.onTouched();
    }
  }

  protected toggleOption(option: DropdownOption<T>, event: MouseEvent): void {
    event.stopPropagation();

    const selectedValues = this.value();
    const nextValue = selectedValues.includes(option.value)
      ? selectedValues.filter((value) => value !== option.value)
      : [...selectedValues, option.value];

    this.value.set(nextValue);
    this.onChange(nextValue);
    this.onTouched();
  }

  protected isSelected(option: DropdownOption<T>): boolean {
    return this.value().includes(option.value);
  }

  private closeMenu(): void {
    if (!this.isOpened()) return;

    this.isOpened.set(false);
    this.searchControl.setValue('', { emitEvent: false });
    this.onTouched();
  }
}
